import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

interface AddManualPageProps {
  websiteId: string;
  onPageAdded: () => void;
}

const AddManualPage = ({ websiteId, onPageAdded }: AddManualPageProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState('page');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate URL
      if (!url) {
        toast.error('Please enter a URL');
        return;
      }

      // Ensure URL has protocol
      let fullUrl = url;
      if (!url.startsWith('http')) {
        fullUrl = 'https://' + url;
      }

      // Create new page in database
      const { error } = await supabase
        .from('website_content')
        .insert({
          website_id: websiteId,
          url: fullUrl,
          title: title || extractTitleFromUrl(fullUrl),
          content_type: contentType,
          is_cornerstone: false,
          content: '', // Add empty content field to satisfy not-null constraint
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast.success('Page added successfully');
      setIsOpen(false);
      onPageAdded();
      
      // Reset form
      setUrl('');
      setTitle('');
      setContentType('page');
    } catch (error: any) {
      console.error('Error adding page:', error);
      toast.error(error.message || 'Failed to add page');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to extract a title from URL
  const extractTitleFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      
      // If it's the homepage
      if (path === '/' || path === '') {
        return 'Homepage';
      }
      
      // Get the last path segment
      const segments = path.split('/').filter(Boolean);
      if (segments.length === 0) {
        return 'Homepage';
      }
      
      let lastSegment = segments[segments.length - 1];
      
      // Remove file extensions
      lastSegment = lastSegment.replace(/\.(html|php|asp|aspx)$/, '');
      
      // Replace hyphens and underscores with spaces
      lastSegment = lastSegment.replace(/[-_]/g, ' ');
      
      // Capitalize first letter of each word
      lastSegment = lastSegment.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      return lastSegment || 'Page';
    } catch (error) {
      console.error(`Error extracting title from URL ${url}:`, error);
      return 'Page';
    }
  };

  return (
    <>
      <Button 
        variant="outline" 
        onClick={() => setIsOpen(true)}
        className="gap-2"
      >
        <Plus className="h-4 w-4" />
        Add Page
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Page</DialogTitle>
            <DialogDescription>
              Add a page that is not in the sitemap but should be included in your content.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="url">Page URL</Label>
              <Input
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/page"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Page Title (Optional)</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Leave empty to generate from URL"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contentType">Content Type</Label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select content type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="page">Page</SelectItem>
                  <SelectItem value="post">Post</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="post_category">Post Category</SelectItem>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="product_category">Product Category</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Page'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddManualPage;